import * as React from "react";
import { cn } from "../../../lib/utils";

export const Input = React.forwardRef(
  (
    {
      className,
      type,
      error,
      label,
      variant = "default",
      size = "md",
      helperText,
      leftIcon,
      rightIcon,
      containerClassName,
      labelClassName,
      labelStyle,
      ...props
    },
    ref,
  ) => {
    const hasError = Boolean(error);
    const errorMessage = typeof error === "string" ? error : undefined;

    const baseClasses =
      "flex w-full rounded-md border shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-normal file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 disabled:cursor-not-allowed disabled:opacity-50 h-8 px-3 py-1.5";

    const variantClasses = {
      default:
        "border-input bg-transparent text-foreground focus-visible:ring-ring",
      dark: "border-gray-600 bg-gray-800 text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent",
      light:
        "border-gray-300 bg-white text-gray-900 placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent",
    };

    const sizeClasses = {
      sm: "h-8 px-2 py-1 text-sm",
      md: "h-9 px-3 py-1 text-sm",
      lg: "h-11 px-4 py-2 text-base",
    };

    const inputClasses = cn(
      baseClasses,
      variantClasses[variant],
      sizeClasses[size],
      hasError && "border-red-500 focus-visible:ring-red-500",
      leftIcon && "pl-10",
      rightIcon && "pr-10",
      // Remove border and rounded corners if className contains specific overrides
      className?.includes("border-t-0") && "border-t-0",
      className?.includes("border-l-0") && "border-l-0",
      className?.includes("rounded-l-none") && "rounded-l-none",
      className,
    );

    const containerClasses = cn("w-full", containerClassName);
    const labelClasses = cn(
      "block text-sm font-normal text-foreground mb-1.5",
      labelClassName,
    );

    const inputElement = (
      <div className="relative">
        {leftIcon && (
          <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
            {leftIcon}
          </div>
        )}
        <input type={type} className={inputClasses} ref={ref} {...props} />
        {rightIcon && (
          <div className="absolute inset-y-0 right-0 flex items-center pr-3">
            {rightIcon}
          </div>
        )}
      </div>
    );

    if (label || errorMessage || helperText) {
      return (
        <div className={containerClasses}>
          {label && (
            <label className={labelClasses} style={labelStyle}>
              {label}
            </label>
          )}
          {inputElement}
          {errorMessage && (
            <p className="mt-1 text-sm text-red-500">{errorMessage}</p>
          )}
          {helperText && !errorMessage && (
            <p className="text-muted-foreground mt-1 text-sm">{helperText}</p>
          )}
        </div>
      );
    }

    return inputElement;
  },
);

Input.displayName = "Input";

export default Input;
